import * as React from "react"

import { cn } from "@/lib/utils"

const Input = React.forwardRef(({ className, type, autoComplete = "off", name, onFocus, readOnly, ...props }, ref) => {
  // Random name + id defeat browser autofill which indexes by name/id attribute
  const safeName = name || React.useMemo(() => `f-${Math.random().toString(36).slice(2, 10)}`, []);
  // The "readOnly until focus" trick — the only reliable way to fully suppress Chrome's autofill bubble
  const [isReadOnly, setIsReadOnly] = React.useState(true);
  React.useEffect(() => {
    if (readOnly) return;
    // Release readOnly after first paint so the field is interactive
    const t = setTimeout(() => setIsReadOnly(false), 50);
    return () => clearTimeout(t);
  }, [readOnly]);
  const handleFocus = (e) => {
    setIsReadOnly(false);
    if (onFocus) onFocus(e);
  };
  // Skip the trick for non-text inputs (checkbox, radio, file, range, color, hidden, date, etc.)
  const skipTrick = type && !['text', 'email', 'tel', 'url', 'number', 'password', 'search', undefined].includes(type);
  return (
    <input
      type={type}
      name={safeName}
      autoComplete="new-password"
      autoCorrect="off"
      autoCapitalize="off"
      spellCheck="false"
      data-form-type="other"
      data-lpignore="true"
      data-1p-ignore="true"
      data-bwignore="true"
      aria-autocomplete="none"
      role="presentation"
      readOnly={readOnly || (!skipTrick && isReadOnly)}
      onFocus={handleFocus}
      className={cn(
        "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-0 focus-visible:border-slate-400 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        className
      )}
      ref={ref}
      {...props} />
  );
})
Input.displayName = "Input"

export { Input }
